import { today } from '@seen/core/dates'
import {
  AdmissionEncounter,
  type AdmissionStatus,
} from '@seen/core/models/encounters/admission-encounter'
import { getReferenceString } from '@seen/core/references'
import { Button } from '@seen/ui'
import { useQueryClient } from '@tanstack/react-query'
import { type JSX, useEffect } from 'react'
import { z } from 'zod'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormLabel } from '#src/components/core/form/FormLabel.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { LocationComboboxButton } from '#src/components/LocationComboboxButton.tsx'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useUpsertResourceFormDialogStore } from '#src/hooks/useUpsertResourceFormDialogStore.ts'

interface AddAdmissionProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddAdmission({
  open,
  onOpenChange,
}: AddAdmissionProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const participant = useCurrentParticipant()
  const queryClient = useQueryClient()
  const openUpsertResourceFormDialog = useUpsertResourceFormDialogStore(
    (state) => state.open
  )

  useEffect(() => {
    if (open) {
      void queryClient.invalidateQueries({
        queryKey: ['Location', 'searchExtended'],
        refetchType: 'active',
      })
    }
  }, [open, queryClient])

  const form = useForm({
    schema: z
      .object({
        location: z.string({ required_error: 'Location is required' }),
        startTime: z.string({ required_error: 'Start date is required' }),
        endTime: z.string().nullable(),
        status: z.enum(AdmissionEncounter.ADMISSION_STATUS_VALUES, {
          required_error: 'Status is required',
        }),
      })
      .superRefine((data, ctx) => {
        if (data.endTime && new Date(data.endTime) < new Date(data.startTime)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'End date must be after start date',
            path: ['endTime'],
          })
        }
      }),
    defaultValues: {
      location: '',
      status: 'in-progress',
      startTime: today(),
      endTime: null,
    },
  })

  const createMutation = useCreateMutation<AdmissionEncounter>()

  const submit = form.handleSubmit(async (values, event) => {
    event?.preventDefault()

    if (!participant) {
      throw new Error('Cannot add admission: Participant not found')
    }

    const newAdmission = new AdmissionEncounter({
      status: values.status,
      patient: participant.reference,
      location: { reference: values.location },
      startTime: values.startTime,
      endTime: values.endTime ?? undefined,
    })

    createMutation.mutate(newAdmission, {
      onSuccess: () => {
        onOpenChange(false)
        form.reset()
      },
    })
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDisplay>
        <DialogHeader className="py-4 text-lg">Add Admission</DialogHeader>
        <DialogContent className="pt-0">
          <Form onSubmit={submit}>
            <div className="space-y-8">
              <div className="flex flex-1 gap-4">
                <div className="flex flex-col">
                  <FormLabel className="text-sm font-medium">
                    Location
                  </FormLabel>
                  <FormField
                    control={form.control}
                    name="location"
                    render={({ field: { value, onChange, ...fieldRest } }) => (
                      <LocationComboboxButton
                        {...fieldRest}
                        value={value ? { reference: value } : undefined}
                        onChange={(value) => onChange(value?.reference ?? '')}
                        disabled={createMutation.isPending}
                        onEdit={(l) => {
                          openUpsertResourceFormDialog({
                            resource: l.toFhirResource(),
                            title: 'Edit Location',
                            onSubmit: (r) => {
                              onChange(getReferenceString(r))
                            },
                          })
                        }}
                        onCreate={() => {
                          openUpsertResourceFormDialog({
                            resource: { resourceType: 'Location' },
                            title: 'Create Location',
                            onSubmit: (r) => {
                              onChange(getReferenceString(r))
                            },
                          })
                        }}
                      />
                    )}
                  />
                </div>
                <div className="flex flex-col">
                  <FormLabel className="text-sm font-medium">
                    Start Date
                  </FormLabel>
                  <FormField
                    control={form.control}
                    name="startTime"
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        onChange={(value) => field.onChange(value ?? '')}
                      />
                    )}
                  />
                </div>
                <div className="flex flex-col">
                  <FormLabel className="text-sm font-medium">
                    End Date
                  </FormLabel>
                  <FormField
                    control={form.control}
                    name="endTime"
                    render={({ field }) => (
                      <DatePicker {...field} allowUndefined />
                    )}
                  />
                </div>
                <div className="flex flex-col">
                  <FormLabel className="text-sm font-medium">Status</FormLabel>
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <Combobox<AdmissionStatus>
                        options={AdmissionEncounter.ADMISSION_STATUS_OPTIONS}
                        onSelect={field.onChange}
                      >
                        <Button variant="outline">{field.value}</Button>
                      </Combobox>
                    )}
                  />
                </div>
              </div>
              <Button
                type="submit"
                loading={createMutation.isPending}
                disabled={isReadOnly}
              >
                Add Admission
              </Button>
            </div>
          </Form>
        </DialogContent>
      </DialogDisplay>
    </Dialog>
  )
}
